import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import React, { FC, MouseEvent, ReactNode } from 'react';

import useModalHook from '../useModalHook';
import TitleBar from './titleBar';

type Props = {
  isOpen?: boolean;
  content?: ReactNode;
  handleOnClose?: (e: MouseEvent<HTMLButtonElement>) => void;
  title?: ReactNode;
  titleIcon?: string;
  titleIconFilled?: boolean;
  canClickOutside?: boolean;
  steps?: number;
  step?: number;
  className?: string | undefined;
  modalClassName?: string;
  isShifted?: boolean;
};

const modalBackgroundId = 'modal-background';

/**
 * Pure component used for story book
 * Use the redux-modal wrapper
 * */
const BaseModal: FC<Props> = ({
  canClickOutside = true,
  className = undefined,
  content = undefined,
  handleOnClose = undefined,
  isOpen = false,
  isShifted = true,
  modalClassName = '',
  step = undefined,
  steps = undefined,
  title = undefined,
  titleIcon = undefined,
  titleIconFilled = false,
}) => {
  useModalHook(isOpen || false);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="modal"
          animate={{ opacity: 1 }}
          className={clsx(
            modalClassName,
            'fixed top-0 z-[131] h-screen w-screen bg-gray-600 bg-opacity-30'
          )}
          exit={{ opacity: 0 }}
          id={modalBackgroundId}
          initial={{ opacity: 0 }}
          /** Only close the modal if the user clicks on the background */
          onClick={(e) => {
            e.stopPropagation();

            if (canClickOutside) {
              if ((e.target as HTMLDivElement).id === modalBackgroundId) {
                handleOnClose?.(e as any);
              }
            }
          }}
        >
          <motion.div
            key="modal-children"
            animate={{ top: '50%' }}
            className={clsx(
              className,
              'ml-28' && isShifted,
              'fixed left-2/4 top-2/4 z-70 flex flex-col',
              'bg-white',
              '-translate-x-2/4 -translate-y-2/4 transform'
            )}
            exit={{ top: '-50%' }}
            id="modal"
            initial={{ top: '-50%' }}
            style={{ minHeight: '10vh', minWidth: '10vh' }}
          >
            <div className="min-w-[400px] max-w-[600px] p-5">
              {title && (
                <TitleBar
                  className="mb-4"
                  handleOnClose={handleOnClose}
                  step={step}
                  steps={steps}
                  title={title}
                  titleIcon={titleIcon}
                  titleIconFilled={titleIconFilled}
                />
              )}
              <div>{content}</div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BaseModal;
